// Only operational metadata belongs here. Never send prompts, titles, resource
// URLs, server messages, stacks or raw diagnostics to the monitoring service.
export interface AgentTelemetryContext {
  sessionId?: string;
  assistantMessageId?: string;
  artifactId?: string;
  attemptId?: string;
  operation?: string;
  skill?: string;
  phase?: string;
  outcome?: string;
  errorCode?: string;
  visibility?: string;
  elapsedMs?: number;
  status?: number;
  afterSeq?: number;
  lastSeq?: number;
  chunkCount?: number;
  lastByteAgoMs?: number;
  artifactCount?: number;
  version?: number;
  width?: number;
  height?: number;
}

export interface AgentTelemetryEvent {
  name: string;
  categories: Record<string, string>;
  metrics: Record<string, number>;
}

const categoryKeys = [
  'sessionId',
  'assistantMessageId',
  'artifactId',
  'attemptId',
  'operation',
  'skill',
  'phase',
  'outcome',
  'errorCode',
  'visibility',
] as const;
const metricKeys = [
  'elapsedMs',
  'status',
  'afterSeq',
  'lastSeq',
  'chunkCount',
  'lastByteAgoMs',
  'artifactCount',
  'version',
  'width',
  'height',
] as const;

const knownErrors = new Set([
  'EXECUTION_OWNER_LOST',
  'VSLIDE_HOSTED_PUBLISH_FAILED',
  'MODEL_CAPACITY_LIMIT',
  'MODEL_REQUEST_REJECTED',
  'MODEL_UPSTREAM_ERROR',
  'MODEL_UPSTREAM_TIMEOUT',
  'MODEL_UPSTREAM_UNAVAILABLE',
  'UPSTREAM_TEMPORARY_FAILURE',
  'STREAM_ENDED_EARLY',
  'STREAM_SEQUENCE_GAP',
  'STREAM_UNAVAILABLE',
  'RENDER_FAILED',
  'IMAGE_LOAD_FAILED',
  'NO_PREVIEW_RESOURCE',
  'PREVIEW_UNSUPPORTED',
  'ARTIFACT_RESOURCE_UNAVAILABLE',
  'ARTIFACT_SPEC_MISSING',
  'AbortError',
  'TypeError',
  'SyntaxError',
]);

export function agentTelemetryErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return 'UNKNOWN';
  const { code, name } = error as { code?: unknown; name?: unknown };
  const candidate = typeof code === 'string' ? code : name;
  return typeof candidate === 'string' &&
    (knownErrors.has(candidate) || /^HTTP_[1-5]\d{2}$/.test(candidate))
    ? candidate
    : 'UNKNOWN';
}

export function agentPreviewErrorCode(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    (error as { message?: unknown }).message ===
      'VInfo Graphics runtime failed to load'
  ) {
    return 'RUNTIME_UNAVAILABLE';
  }
  return agentTelemetryErrorCode(error);
}

let sink: ((event: AgentTelemetryEvent) => void) | undefined;

export function createAgentAttemptId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Wired to the site's existing Slardar client only in the browser entry point.
// Tests can replace the sink without loading a browser SDK or sending requests.
export function setAgentTelemetrySink(next: typeof sink): void {
  sink = next;
}

export function reportAgentEvent(
  event: 'request' | 'stream' | 'message_terminal' | 'preview' | 'export',
  context: AgentTelemetryContext
): void {
  const categories: Record<string, string> = {};
  const metrics: Record<string, number> = {};
  for (const key of categoryKeys) {
    const value = context[key];
    if (typeof value === 'string' && /^[\w.-]{1,128}$/.test(value)) {
      categories[key] = value;
    }
  }
  for (const key of metricKeys) {
    const value = context[key];
    if (typeof value === 'number' && Number.isFinite(value))
      metrics[key] = value;
  }
  const record = { name: `agent_${event}`, categories, metrics };
  try {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.debug(`[AgentTelemetry] ${JSON.stringify(record)}`);
    } else {
      sink?.(record);
    }
  } catch {
    // Monitoring must not change request/render outcomes or log a failed payload.
    console.warn('[AgentTelemetry] reporting failed');
  }
}
